import type { IProjectMetadata } from '@Hub/Search/types';
import type { SearchTab } from '@Hub/SuperHubSearch/types';
import type { SearchResults } from '@readme/api-contracts/schemas/search';
import type { ProjectClientSide } from '@readme/backend/models/project/types';

import { SEARCH_RESULTS_PER_PAGE_DEFAULT } from '@readme/api-contracts/constants/search';
import pick from 'lodash/pick';

import { TabNames } from '@Hub/Search/types';

import { superHubStore } from '../SuperHub';
import { createBoundedUseStore, actionLog } from '../util';
import { createCrossFrameStore } from '../util/createCrossFrameStore';

interface ProjectListItem {
  internal: ProjectClientSide['internal'];
  name: string;
  subdomain: string;
}

interface SearchStoreState {
  /** Active tab for search results */
  activeTab: TabNames;
  /** Tabs to display based on available project modules */
  displayedTabs: string[];
  /** Cached enabled modules map, used to rebuild tabs when subdomain filters change */
  enabledModules: Record<string, Record<string, boolean>>;
  /** Loading state for search results (from SWR fetcher) */
  isLoading: boolean;
  /** Current search results page */
  page: number;
  /** Search results per page */
  perPage: number;
  /** List of projects to display in the project filter */
  projectList: ProjectListItem[];
  /** Query for search */
  prompt: string;
  /** Search results */
  results: SearchResults;
  /** Used to filter search results by type (section) for tabs */
  sectionFilter: string;
  /** Used to filter search results by subdomain (for Enterprise Groups) */
  subdomainFilters: string[];
}

interface SearchStoreAction {
  /**
   * Takes project metadata and available modules
   * and builds available tabs
   */
  buildDisplayedTabs: (projectsMeta: IProjectMetadata[]) => void;

  /**
   * Takes project metadata and available modules
   * and builds available tabs
   */
  buildProjectList: (project: ProjectClientSide) => void;

  /**
   * Resets state back to the last "initialized" state
   */
  reset: () => void;

  /**
   * Sets the current search results page
   * @example
   * setPage(2);
   */
  setPage: (page: number) => void;

  /**
   * Sets the search results
   * @example
   * setResults({ data: [], total: 0 });
   */
  setResults: (results: SearchResults, isLoading: boolean) => void;

  /**
   * Set subdomain filters
   * @example
   * setSubdomainFilters(['subdomain']);
   */
  setSubdomainFilters: (subdomains: string[]) => void;

  /**
   * Updates the state's stored section filter and active tab
   * @example
   * setTab({ moduleName: 'guides', tabName: 'Guides' });
   */
  setTab: ({ searchModuleName, moduleName, label }: SearchTab) => void;

  /**
   * Updates the state's stored search/AI prompt
   * @example
   * updatePrompt('upload an API spec');
   */
  updatePrompt: (input: string) => void;
}

type SearchStore = SearchStoreAction & SearchStoreState;

/**
 * Converts a list of project schema shells into
 * an object that contains a key for the project and value for all enabled/disabled modules
 * for that project.
 * @returns {object} { developers: { guides: true, reference: false } }
 */
const enableModuleMap = (metadata: IProjectMetadata[]) => {
  return (metadata || []).reduce(
    (obj, { subdomain, modules }) => {
      obj[subdomain] = modules;
      return obj;
    },
    {} as Record<string, Record<string, boolean>>,
  );
};

/**
 * Util that builds the array of tabs to display based on the enabled modules and subdomain filters
 * @returns {string[]} ['Guides', 'Reference']
 */
const buildDisplayTabs = (enabledModules: Record<string, Record<string, boolean>>, subdomainFilters: string[]) => {
  // We'll pick the selected modules based on any subdomain filters
  const selectedModules = subdomainFilters?.length ? pick(enabledModules, subdomainFilters) : enabledModules;

  // Loop over enabled modules and match subdomain filters to keys
  const values = Object.values(selectedModules);
  const modules = Object.values(values).reduce((obj, module) => {
    Object.keys(module).forEach(key => {
      if (obj[key]) obj[key] = !!(obj[key] || module[key]);
      else obj[key] = !!module[key];
    });

    return obj;
  }, {});

  return Object.entries(modules)
    .filter(([, v]) => v)
    .map(([k]) => k);
};

const initialState: SearchStoreState = {
  activeTab: TabNames.All,
  displayedTabs: [],
  enabledModules: {},
  prompt: '',
  sectionFilter: '',
  subdomainFilters: [],
  projectList: [],
  page: 1,
  perPage: SEARCH_RESULTS_PER_PAGE_DEFAULT,
  results: {
    data: [],
    page: 1,
    per_page: SEARCH_RESULTS_PER_PAGE_DEFAULT,
    paging: {
      first: null,
      last: null,
      next: null,
      previous: null,
    },
    total: 0,
  },
  isLoading: false,
};

/**
 * Add store description and example
 */
export const searchStore = createCrossFrameStore<SearchStore>('SearchStore', set => {
  const resetState = initialState;

  return {
    ...initialState,

    reset: () => {
      set(resetState, false, actionLog('reset'));
    },

    setTab(tab) {
      set(
        state => {
          state.sectionFilter = tab.searchModuleName || tab.moduleName;
          state.activeTab = tab.label;
          state.page = 1;
        },
        false,
        actionLog('setTab', tab),
      );
    },

    buildDisplayedTabs(projectsMeta) {
      set(
        state => {
          const newEnabledModules = enableModuleMap(projectsMeta);
          const newDisplayedTabs = buildDisplayTabs(newEnabledModules, state.subdomainFilters);

          state.enabledModules = newEnabledModules;
          state.displayedTabs = newDisplayedTabs;
        },
        false,
        actionLog('buildDisplayedTabs', projectsMeta),
      );
    },

    buildProjectList(project) {
      const { role, isAuthenticated, permissions } = superHubStore.getState().user;
      const isSuperHubAdminOrEditor = role === 'admin' || role === 'editor';

      set(
        state => {
          let projectList: ProjectClientSide[] = [];
          let groupId = '';

          if (project.childrenProjects && project.childrenProjects.length > 0) {
            projectList = project.childrenProjects as ProjectClientSide[];
            groupId = project._id;
          } else if (project.siblings && project.siblings.length > 0) {
            projectList = project.siblings as ProjectClientSide[];
            groupId = project.parent?._id || '';
          }

          /**
           * Filtered list of projects that the current user can search across.
           * - Public projects are always included.
           * - Private projects included when user is authenticated.
           * - Private projects included when logged in user is admin/editor.
           */
          const trimmedProjectList = projectList
            .filter(p => {
              /** When internal is empty, project is public. */
              const isPublic = !p.internal;
              return (
                isPublic ||
                isSuperHubAdminOrEditor ||
                // Can user access a password-protected project?
                isAuthenticated ||
                // Can user access child project?
                permissions.includes(p._id) ||
                // Does user access group project?
                permissions.includes(groupId)
              );
            })
            .map(p => ({
              name: p.name,
              subdomain: p.subdomain,
              internal: p.internal,
            }));

          state.projectList = trimmedProjectList;
        },
        false,
        actionLog('buildProjectList', project),
      );
    },

    setResults(results, isLoading) {
      set(
        state => {
          state.results = results;
          state.isLoading = isLoading;
        },
        false,
        actionLog('setResults', results),
      );
    },

    setPage(page) {
      set(
        state => {
          state.page = Math.max(1, page);
        },
        false,
        actionLog('setPage', page),
      );
    },

    setSubdomainFilters(subdomains) {
      set(
        state => {
          state.subdomainFilters = subdomains;
          state.displayedTabs = buildDisplayTabs(state.enabledModules, subdomains);
          state.page = 1;

          // Reset to "All" tab if the active tab is no longer available
          if (state.activeTab !== TabNames.All && !state.displayedTabs.includes(state.sectionFilter)) {
            state.activeTab = TabNames.All;
            state.sectionFilter = '';
          }
        },
        false,
        actionLog('setSubdomainFilters', subdomains),
      );
    },

    updatePrompt(input) {
      set(
        state => {
          state.prompt = input;
          state.page = 1;
        },
        false,
        actionLog('updatePrompt', input),
      );
    },
  };
});

/**
 * Bound react hook to access our project store. Must be called within a React
 * component. To access the store outside of React, use `searchStore` instead.
 */
export const useSearchStore = createBoundedUseStore(searchStore);
